import React, { useState, useContext } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Slider } from "../ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import { ChevronDown, ChevronRight, Plus, Trash2, Package, Info } from "lucide-react";
import { FORMULAS, UNIT_LABEL, fmtEUR } from "../../lib/calculator";
import { AutoSourcesContext } from "../../lib/autoSource";
import CodeCombobox from "./CodeCombobox";
import NumF from "./NumF";
import { EditableDescription } from "./EditableDescription";

const TYPE_LABEL = { wall: "M", ceiling: "P", floor: "V", piece: "S", linear: "L", hour: "U", fixed: "F", custom: "A" };
const DEFAULT_PC_NOTE = "Default professional painting price, editable per project";

export default function MeasurementRow({
  m, idx, priceCodes, onChange, onRemove, onDuplicate,
  onAddDeduction, onUpdDeduction, onRemDeduction,
  autoSources: autoSourcesProp,   // v6 F16 S4 — override via prop (optioneel)
}) {
  // v6 F16 S4 — Auto-source catalog via Context (default) of via prop.
  const autoSourcesCtx = useContext(AutoSourcesContext);
  const autoSources = autoSourcesProp || autoSourcesCtx || {};
  const [showDed, setShowDed] = useState(false);
  const f = m.formula;
  const showL = f === "wall" || f === "ceiling" || f === "floor" || f === "linear";
  const showWidth = f === "ceiling" || f === "floor";
  const showHeight = f === "wall";
  const showQty = f === "piece" || f === "hour" || f === "custom";
  const showCoats = f === "wall" || f === "ceiling" || f === "floor";
  const showDeductions = f === "wall";

  const pc = m.price_code_id ? priceCodes.find((p) => p.id === m.price_code_id) : null;
  const effPrice = m.unit_price_override ?? pc?.default_price ?? 0;
  const formulaLabel = FORMULAS.find((x) => x.value === f)?.label || f;
  // v6 F1 — show a small Info tooltip when the bound price-code carries a
  // non-boilerplate helper note (e.g. S-04.10–13 raamformaten S/M/L/XL).
  const helperNote = (pc?.notes && pc.notes !== DEFAULT_PC_NOTE) ? pc.notes : "";

  // Live computed amounts
  const L = +m.length || 0, H = +m.height || 0, W = +m.width || 0, Q = +m.quantity || 0;
  const mult = Math.max(1, +m.multiplier || 1);
  let gross = 0;
  if (f === "wall") gross = L * H;
  else if (f === "ceiling" || f === "floor") gross = L * W;
  else if (f === "linear") gross = L;
  else if (f === "piece" || f === "hour" || f === "custom") gross = Q;
  else if (f === "fixed") gross = 1;
  const dedu = (m.deductions || []).reduce((s, d) => ((+d.area || 0) >= 1.0 ? s + (+d.area || 0) : s), 0);
  const netUnit = Math.max(0, (f === "wall" ? gross - dedu : gross));
  const netTotal = netUnit * mult;
  const formulaText =
    f === "wall" ? `(${L} × ${H} − ${dedu.toFixed(2)}) × ${mult} = ${netTotal.toFixed(2)} m²`
    : f === "ceiling" || f === "floor" ? `(${L} × ${W}) × ${mult} = ${netTotal.toFixed(2)} m²`
    : f === "linear" ? `${L} × ${mult} = ${netTotal.toFixed(2)} lm`
    : `${Q} × ${mult} = ${netTotal.toFixed(2)}`;

  const totalExcl = netTotal * (m.unit_price_override ?? pc?.default_price ?? 0);
  const typeChar = TYPE_LABEL[f] || "A";
  const dimText = f === "wall" ? `${L}×${H}` : (f === "ceiling" || f === "floor") ? `${L}×${W}` : f === "linear" ? `${L}` : `${Q}st`;

  // v6 F36 — Mobiele prijs-samenvatting per regel. Op iPhone/smalle schermen
  // moet de gebruiker snel kunnen zien: hoeveelheid × eenheidsprijs = totaal.
  // Format (NL-BE): "60,00 m² × € 16,00/m² = € 960,00".
  // Voor forfait/fixed: "1 forfait × € 95,00 = € 95,00".
  const _qtyFmt = (val) => (Number(val) || 0).toLocaleString("nl-BE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  const _unitLabel = (pc?.unit && UNIT_LABEL[pc.unit]) || (f === "fixed" ? "forfait" : (UNIT_LABEL[f] || ""));
  const priceSummary = (() => {
    if (f === "fixed") {
      return `1 forfait × ${fmtEUR(effPrice)}`;
    }
    const qtyStr = `${_qtyFmt(netTotal)} ${_unitLabel}`.trim();
    const priceStr = `${fmtEUR(effPrice)}${_unitLabel ? `/${_unitLabel}` : ""}`;
    return `${qtyStr} × ${priceStr}`;
  })();

  return (
    <TooltipProvider delayDuration={150}>
    <div className="border border-[#E5E2DC] rounded-xl bg-white" data-testid={`measurement-${m.id}`}>
      {/* COMPACT MOBILE HEADER */}
      <div className="sm:hidden grid grid-cols-[28px_28px_1fr_auto] items-center gap-2 px-3 py-2.5 border-b border-[#E5E2DC]">
        <span className="text-xs font-mono text-[#4F4F4D] text-center">#{idx}</span>
        <span className="w-7 h-7 rounded-lg bg-[#D96C4A] text-white flex items-center justify-center font-bold text-xs" title={formulaLabel}>{typeChar}</span>
        <div className="min-w-0">
          <div className="text-xs font-mono text-[#4F4F4D] truncate">{pc?.code || "—"} · {dimText} · ×{mult}</div>
          <div className="text-sm font-semibold text-[#2C2C2A] leading-snug flex items-start sm:items-center gap-1">
            <EditableDescription
              value={m.description_snapshot || pc?.description || m.description || "Meting"}
              override={m.description_override}
              defaultValue={m.description_snapshot || pc?.description || m.description || "Meting"}
              onOverride={(text) => onChange({ description_override: text })}
              onReset={() => onChange({ description_override: null })}
              testId={`m-desc-${m.id}`}
              className="min-w-0"
            />
            {helperNote && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-[#5D7259] hover:text-[#D96C4A] flex-shrink-0 mt-0.5 sm:mt-0" data-testid={`m-info-${m.id}`} aria-label="Toelichting">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] text-xs">{helperNote}</TooltipContent>
              </Tooltip>
            )}
          </div>
          {/* v6 F16 S4 — Compact auto-source badge in mobile header */}
          {m.auto_source && autoSources[m.auto_source] && (
            <div
              className="text-[10px] text-[#3F5E3F] font-semibold truncate mt-0.5"
              data-testid={`m-auto-hint-mobile-${m.id}`}
            >
              Auto: {autoSources[m.auto_source].label}
              {(+m.multiplier || 1) > 1 && (
                <span className="ml-1 text-[#7F4124]">× {(+m.multiplier || 1)}</span>
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="font-bold text-[#2C2C2A] text-sm">{fmtEUR(totalExcl)}</div>
          <div className="flex gap-0.5 mt-0.5">
            <button onClick={onDuplicate} className="w-7 h-7 rounded-md hover:bg-[#F0EFEA] flex items-center justify-center text-[#4F4F4D]" data-testid={`duplicate-${m.id}`}>
              <Package className="w-3 h-3" />
            </button>
            <button onClick={onRemove} className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-[#D9534F]" data-testid={`remove-${m.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* v6 F36 — Mobiele prijs-samenvatting op eigen lijn onder de header,
          zodat hoeveelheid × eenheidsprijs = totaal duidelijk zichtbaar is op iPhone. */}
      <div
        className="sm:hidden px-3 py-1.5 border-b border-[#E5E2DC] bg-[#FAFAF7] text-[11px] font-mono text-[#2C2C2A]"
        data-testid={`m-price-summary-mobile-${m.id}`}
      >
        {priceSummary}
      </div>

      <div className="p-4 space-y-3">
      <div className="hidden sm:flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-mono text-[#4F4F4D]">#{idx}</span>
          <Badge className="bg-[#D96C4A] text-white text-[10px]">{formulaLabel}</Badge>
          {pc && <span className="font-mono text-[11px] text-[#4F4F4D] truncate">{pc.code}</span>}
          {helperNote && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-[#5D7259] hover:text-[#D96C4A]" data-testid={`m-info-${m.id}-d`} aria-label="Toelichting">
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[280px] text-xs">{helperNote}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={onDuplicate} className="w-8 h-8 rounded-lg hover:bg-[#F0EFEA] flex items-center justify-center text-[#4F4F4D]" title="Dupliceer" data-testid={`duplicate-${m.id}-d`}>
            <Package className="w-3.5 h-3.5" />
          </button>
          <button onClick={onRemove} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-[#D9534F]" data-testid={`remove-${m.id}-d`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider font-bold text-[#5D7259] block mb-1">
          Interne naam <span className="text-[#A8A7A0] normal-case font-normal">(niet zichtbaar op offerte)</span>
        </label>
        <Input value={m.description || ""} onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Bv. Wand 1 noordzijde" className="h-11 rounded-xl" data-testid={`m-desc-input-${m.id}`} />
      </div>

      {/* v6 F42k v14b (final polish) — EditableDescription óók op desktop.
          v14e: geen aparte edit-modus meer — altijd inline bewerkbaar.
          Precedence van getoonde tekst: description_override → description_snapshot
          → pc.description → m.description. defaultValue = snapshot indien aanwezig
          (offerte-historische versie), anders pc.description. */}
      <div className="hidden sm:block">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#B07A4F] mb-1">
          Op offerte <span className="text-[#A8A7A0] normal-case font-normal">(tekst uit prijslijst — klik in het veld om aan te passen voor deze offerte)</span>
        </div>
        <EditableDescription
          value={m.description_snapshot || pc?.description || m.description || "Meting"}
          override={m.description_override}
          defaultValue={m.description_snapshot || pc?.description || m.description || "Meting"}
          onOverride={(text) => onChange({ description_override: text })}
          onReset={() => onChange({ description_override: null })}
          testId={`m-desc-${m.id}-d`}
          className="min-w-0"
        />
      </div>

      {/* v6 F16 S4 — Auto-koppeling hint. Toont "Auto: <NL-label>" wanneer
          de meting via auto_source aan een objectonderdeel gekoppeld is.
          v14at-P10-C — Toont ↺ Herberekenen wanneer user de hoeveelheid
          handmatig heeft overschreven (qty_user_override=true). */}
      {m.auto_source && autoSources[m.auto_source] && (
        <div
          className="flex items-center gap-2 rounded-lg px-3 py-2 bg-[#F2F5EE] border border-[#6B8E6B]/30"
          data-testid={`m-auto-hint-${m.id}`}
        >
          <span className="text-[10px] uppercase tracking-wider font-bold text-[#3F5E3F]">Auto</span>
          <span className="text-xs text-[#3F5E3F] font-medium truncate">
            {autoSources[m.auto_source].label}
          </span>
          {(+m.multiplier || 1) > 1 && (
            <span className="text-[10px] font-semibold text-[#7F4124] px-1.5 rounded bg-[#FFF3E2]">
              × {(+m.multiplier || 1)}
            </span>
          )}
          {m.qty_user_override ? (
            <button
              type="button"
              onClick={() => onChange({ qty_user_override: false, length: 0, width: 0, height: 0, quantity: 0 })}
              className="text-[10px] text-[#7F4124] hover:text-[#D96C4A] underline-offset-2 hover:underline ml-auto font-medium"
              data-testid={`m-herbereken-${m.id}`}
              title="Automatische berekening opnieuw gebruiken"
            >
              ↺ Herberekenen
            </button>
          ) : (
            <span className="text-[10px] text-[#5D7259] italic ml-auto">
              (volgt bron — hoeveelheid automatisch berekend)
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Formule</Label>
          <Select value={m.formula} onValueChange={(v) => onChange({ formula: v })}>
            <SelectTrigger className="h-11 mt-1.5 rounded-xl" data-testid={`m-formula-${m.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{FORMULAS.map((x) => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Prijscode</Label>
          <CodeCombobox
            value={m.price_code_id}
            priceCodes={priceCodes}
            onSelect={(id) => onChange({ price_code_id: id })}
            testId={`m-pricecode-${m.id}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {showL && <NumF label="Lengte (m)" value={m.length} onChange={(v) => onChange(m.auto_source ? { length: v, qty_user_override: true } : { length: v })} testId={`m-length-${m.id}`} />}
        {showWidth && <NumF label="Breedte (m)" value={m.width} onChange={(v) => onChange(m.auto_source ? { width: v, qty_user_override: true } : { width: v })} testId={`m-width-${m.id}`} />}
        {showHeight && <NumF label="Hoogte (m)" value={m.height} onChange={(v) => onChange(m.auto_source ? { height: v, qty_user_override: true } : { height: v })} testId={`m-height-${m.id}`} />}
        {showQty && <NumF label="Aantal" step="1" value={m.quantity} onChange={(v) => onChange(m.auto_source ? { quantity: v, qty_user_override: true } : { quantity: v })} testId={`m-qty-${m.id}`} />}
        <NumF label="Multiplier (×)" step="1" value={m.multiplier} onChange={(v) => onChange({ multiplier: Math.max(1, v || 1) })} testId={`m-multiplier-${m.id}`} />
        <NumF label="Prijs override" step="0.01" placeholder={`€ ${(+effPrice).toFixed(2)}`}
          value={m.unit_price_override ?? ""} onChange={(v) => onChange({ unit_price_override: v === "" ? null : v })}
          testId={`m-price-${m.id}`} allowEmpty />
      </div>

      <div className="bg-[#F0EFEA] rounded-lg px-3 py-2 text-xs font-mono text-[#2C2C2A]" data-testid={`m-formula-text-${m.id}`}>
        = {formulaText}
      </div>

      {showCoats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-[#E5E2DC] pt-3">
          <div>
            <Label className="text-xs">Lagen: <span className="font-bold text-[#D96C4A]">{m.coats}</span></Label>
            <Slider min={1} max={3} step={1} value={[m.coats]} onValueChange={([v]) => onChange({ coats: v })} className="mt-2" data-testid={`m-coats-${m.id}`} />
          </div>
          <div className="flex items-center justify-between border border-[#E5E2DC] rounded-lg px-3 h-11">
            <Label className="text-xs cursor-pointer">Lagen × hvh berekenen</Label>
            <Switch checked={!!m.multiplier_coats} onCheckedChange={(v) => onChange({ multiplier_coats: v })} data-testid={`m-coatmult-${m.id}`} />
          </div>
        </div>
      )}

      {showDeductions && (
        <div className="border-t border-[#E5E2DC] pt-3">
          <button onClick={() => setShowDed(!showDed)} className="flex items-center gap-1 text-sm font-medium text-[#2C2C2A]" data-testid={`toggle-ded-${m.id}`}>
            {showDed ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Aftrekken ({(m.deductions || []).length})
          </button>
          {showDed && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-[#4F4F4D]">Openingen ≥ 1 m² worden afgetrokken.</p>
              {(m.deductions || []).map((d) => (
                <div key={d.id} className="flex gap-2 items-end">
                  <Input value={d.description} onChange={(ev) => onUpdDeduction(d.id, { description: ev.target.value })}
                    placeholder="bv. Raam keuken" className="h-10 rounded-lg flex-1" />
                  <NumF label="m²" value={d.area} onChange={(v) => onUpdDeduction(d.id, { area: v })} compact testId={`ded-area-${d.id}`} />
                  <button onClick={() => onRemDeduction(d.id)} className="h-10 w-10 rounded-lg text-[#D9534F] hover:bg-red-50 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button variant="outline" onClick={onAddDeduction} className="h-9 rounded-lg gap-1 text-xs" data-testid={`add-ded-${m.id}`}>
                <Plus className="w-3 h-3" /> Aftrek
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-[#4F4F4D] border-t border-[#E5E2DC] pt-2">
        <span>{pc ? `${UNIT_LABEL[pc.unit]} · € ${(+effPrice).toFixed(2)} excl. BTW` : "Geen prijscode"}</span>
      </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
